import React, { useEffect, useState } from "react";

import {
    CalendarMonth as CalendarIcon,
    Schedule as ScheduleIcon,
    Sync as SyncIcon,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import { ClipLoader } from "react-spinners";

import {
    getBigquerySettings,
    postBigquerySettings,
} from "helpers/Sources/bigquery/bigquerySource";
import { getSourceDetail } from "helpers/Sources/getSource";

import SwalAlertComp from "components/SwalAlert";

import { TabProps } from "../../../SourceModal.types";

interface ScheduleOption {
    label: string;
    value: string;
}

const toDatetimeLocal = (value: string) => {
    const match = value
        .trim()
        .match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) {
        return value.replace(" ", "T").slice(0, 16);
    }
    const [, day, month, year, hours, minutes] = match;
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const toApiDatetime = (value: string) => {
    const match = value
        .trim()
        .match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) {
        return value;
    }
    const [, year, month, day, hours, minutes, seconds = "00"] = match;
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const ScheduleTab: React.FC<TabProps> = ({ source, language, onRefresh }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const [scheduleOptions, setScheduleOptions] = useState<ScheduleOption[]>(
        [],
    );
    const [readingOptions, setReadingOptions] = useState<ScheduleOption[]>([]);

    const [selectedSchedule, setSelectedSchedule] = useState<string>("");
    const [selectedReading, setSelectedReading] = useState<string>("");
    const [scheduleStartDatetime, setScheduleStartDatetime] =
        useState<string>("");

    const [initialSchedule, setInitialSchedule] = useState<string>("");
    const [initialReading, setInitialReading] = useState<string>("");
    const [initialDatetime, setInitialDatetime] = useState<string>("");
    const [initialDatetimeLocal, setInitialDatetimeLocal] =
        useState<string>("");

    const sourceId = source?.id;

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [settingsRes, detailRes] = await Promise.all([
                getBigquerySettings(),
                getSourceDetail(sourceId),
            ]);

            if (settingsRes.status) {
                const schedules = settingsRes.data?.schedules;
                const reading = settingsRes.data?.reading;

                if (schedules) {
                    setScheduleOptions(
                        Object.keys(schedules).map((key) => ({
                            label: schedules[key],
                            value: key,
                        })),
                    );
                }
                if (reading) {
                    setReadingOptions(
                        Object.keys(reading).map((key) => ({
                            label: reading[key],
                            value: key,
                        })),
                    );
                }
            }

            if (detailRes.status) {
                const extras = detailRes.data?.extras || [];
                const scheduleVal =
                    extras.find(
                        (e: { key: string; value: any }) =>
                            e.key === "schedule",
                    )?.value || "";
                const readingVal =
                    extras.find(
                        (e: { key: string; value: any }) => e.key === "reading",
                    )?.value || "";
                const datetimeVal =
                    extras.find(
                        (e: { key: string; value: any }) =>
                            e.key === "schedule_start_datetime",
                    )?.value || "";

                setSelectedSchedule(scheduleVal);
                setSelectedReading(readingVal);
                setInitialSchedule(scheduleVal);
                setInitialReading(readingVal);

                if (datetimeVal) {
                    const formatted = toDatetimeLocal(datetimeVal);
                    setScheduleStartDatetime(formatted);
                    setInitialDatetime(formatted);
                    setInitialDatetimeLocal(
                        extras.find(
                            (e: { key: string; value: any }) =>
                                e.key === "schedule_start_datetime",
                        )?.value || "",
                    );
                }
            }
        } catch {
            // handled by createRequest
        }
        setIsLoading(false);
    };

    useEffect(() => {
        if (sourceId) {
            fetchData();
        }
    }, [sourceId]);

    const hasChanges =
        selectedSchedule !== initialSchedule ||
        selectedReading !== initialReading ||
        scheduleStartDatetime !== initialDatetime;

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const datetimeForApi = scheduleStartDatetime
                ? toApiDatetime(scheduleStartDatetime)
                : null;

            const response = await postBigquerySettings({
                schedule: selectedSchedule || null,
                reading: selectedReading || null,
                source_id: sourceId,
                schedule_start_datetime: datetimeForApi,
            });

            if (response.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response.message || "Schedule settings updated.",
                    icon: "success",
                    timer: 3000,
                });
                setInitialSchedule(selectedSchedule);
                setInitialReading(selectedReading);
                setInitialDatetime(scheduleStartDatetime);
                setInitialDatetimeLocal(datetimeForApi || "");
                onRefresh?.();
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message:
                        response.message ||
                        "Failed to update schedule settings.",
                    icon: "error",
                });
            }
        } catch {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: "An error occurred while saving schedule settings.",
                icon: "error",
            });
        }
        setIsSaving(false);
    };

    const getScheduleLabel = (value: string) =>
        scheduleOptions.find((o) => o.value === value)?.label || value;

    const getReadingLabel = (value: string) =>
        readingOptions.find((o) => o.value === value)?.label || value;

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Schedule Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Configure the data update period, method, and start time for
                    this BigQuery integration.
                </Typography>
            </Box>
            {isLoading && (
                <Stack
                    justifyContent="center"
                    alignItems="center"
                    sx={{ py: 6 }}
                >
                    <CircularProgress color="primary" size={24} />
                </Stack>
            )}

            {!isLoading && initialSchedule && (
                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        backgroundColor: "grey.50",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        Current Configuration
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        <Chip
                            icon={<ScheduleIcon />}
                            label={`Period: ${getScheduleLabel(initialSchedule)}`}
                            variant="outlined"
                            size="small"
                            onClick={() => {}}
                        />
                        <Chip
                            icon={<SyncIcon />}
                            label={`Method: ${getReadingLabel(initialReading)}`}
                            variant="outlined"
                            size="small"
                            onClick={() => {}}
                        />
                        {initialDatetime && (
                            <Chip
                                icon={<CalendarIcon />}
                                label={`Schedule Date & Time: ${initialDatetimeLocal}`}
                                variant="outlined"
                                size="small"
                                onClick={() => {}}
                            />
                        )}
                    </Stack>
                </Box>
            )}
            {!isLoading && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                    }}
                >
                    <FormControl fullWidth>
                        <InputLabel id="schedule-select-label">
                            Data Update Period
                        </InputLabel>
                        <Select
                            labelId="schedule-select-label"
                            value={selectedSchedule}
                            label="Data Update Period"
                            onChange={({ target: { value } }) =>
                                setSelectedSchedule(value as string)
                            }
                        >
                            {scheduleOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel id="reading-select-label">
                            Data Update Method
                        </InputLabel>
                        <Select
                            labelId="reading-select-label"
                            value={selectedReading}
                            label="Data Update Method"
                            onChange={({ target: { value } }) =>
                                setSelectedReading(value as string)
                            }
                        >
                            {readingOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Schedule Date & Time"
                        type="datetime-local"
                        value={scheduleStartDatetime}
                        onChange={(e) =>
                            setScheduleStartDatetime(e.target.value)
                        }
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>
            )}
            {!isLoading && (
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 4 }}>
                    <div className="position-relative">
                        {isSaving && (
                            <div className="circle-loader max-height-45">
                                <ClipLoader color={"#000"} size={17} />
                            </div>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={
                                isSaving ||
                                !selectedSchedule ||
                                !selectedReading ||
                                !hasChanges
                            }
                            sx={{ minWidth: 120 }}
                        >
                            {isSaving ? "Saving..." : "Save"}
                        </Button>
                    </div>
                </Stack>
            )}
        </Box>
    );
};

export default ScheduleTab;
